import { ElementType } from 'react';

import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useFormatPickerModal } from './format-picker-modal';
import { getFormatPreview, isMatchingPreset } from './format-presets';

import type { FormatPresetData } from '../helpers';

type FormatDropdownProps = {
  format?: string;
  formatLanguage?: string;
  presets?: FormatPresetData[];
  onSavePreset?: (preset: Omit<FormatPresetData, 'id'>) => void;
  onEditPreset?: (preset: FormatPresetData) => void;
  onRemovePreset?: (id: string) => void;
  onFormatChange: (format: string, formatLanguage?: string) => void;
  trigger: ElementType;
  triggerProps: Record<string, any>;
};

export const FormatDropdown = ({
  format,
  formatLanguage,
  presets = [],
  onSavePreset,
  onEditPreset,
  onRemovePreset,
  onFormatChange,
  trigger,
  triggerProps,
}: FormatDropdownProps) => {
  const { t } = useScopedTranslate(locale);
  const showFormatPicker = useFormatPickerModal();
  const normalizedFormat = format && format !== 'undefined' ? format : '';
  const hasActions = Boolean(onEditPreset || onRemovePreset);

  return (
    <Menu
      trigger={trigger}
      triggerProps={triggerProps}
      placement="bottom-start"
      width={pxToRem(256)}
    >
      {({ close }) => {
        const defaultPreset = presets.find((preset) => preset.isDefault);
        const isDefaultSelected = !normalizedFormat;
        return (
          <>
            <Menu.Item
              label={t('default')}
              helpText={defaultPreset?.name}
              isActive={isDefaultSelected}
              extraIconFullOpacity
              extraIcon={
                isDefaultSelected ? (
                  <IconCheckmark size={24} color="buttonIconPositive" />
                ) : undefined
              }
              onSelect={() => {
                onFormatChange('', undefined);
                close();
              }}
            />
            {presets.map((preset) => {
              const isSelected = isMatchingPreset(preset, normalizedFormat, formatLanguage);
              const preview = getFormatPreview(preset.format, preset.formatLanguage);
              return (
                <Menu.Item
                  key={preset.id}
                  label={preset.name}
                  helpText={preview}
                  isActive={isSelected}
                  extraIconFullOpacity
                  extraIcon={
                    <Stack orientation="horizontal" space={1} align="center">
                      {isSelected && <IconCheckmark size={24} color="buttonIconPositive" />}
                      {hasActions && !preset.isSystem && (
                        <PresetActions
                          preset={preset}
                          onEdit={onEditPreset}
                          onRemove={onRemovePreset}
                        />
                      )}
                    </Stack>
                  }
                  onSelect={() => {
                    onFormatChange(preset.format, preset.formatLanguage);
                    close();
                  }}
                />
              );
            })}
            <Menu.Separator />
            <Menu.Item
              label={t('custom')}
              helpText={t('createNew')}
              icon={<IconAddPlus size={24} color="elementPurple80" />}
              onSelect={() => {
                close();
                showFormatPicker({
                  onSavePreset,
                  onApply: (customFormat, language) => onFormatChange(customFormat, language),
                });
              }}
            />
          </>
        );
      }}
    </Menu>
  );
};

const PresetActions = ({
  preset,
  onEdit,
  onRemove,
}: {
  preset: FormatPresetData;
  onEdit?: (preset: FormatPresetData) => void;
  onRemove?: (id: string) => void;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Menu
      placement="right-start"
      trigger={IconButton}
      triggerProps={{
        variant: 'ghost-purple',
        size: 'sm',
        icon: <IconMoreVertical size={16} />,
      }}
    >
      {onEdit && (
        <Menu.Item
          label={t('edit')}
          icon={<IconEdit />}
          isSubmenu
          onSelect={() => onEdit(preset)}
        />
      )}
      {onRemove && (
        <Menu.Item
          label={t('delete')}
          icon={<IconTrash />}
          variant="danger"
          isSubmenu
          isDisabled={preset.isDefault}
          tooltip={preset.isDefault ? t('deleteDefaultTooltip') : undefined}
          onSelect={() => onRemove(preset.id)}
        />
      )}
    </Menu>
  );
};

const locale = {
  en: {
    default: 'Default',
    custom: 'Custom',
    createNew: 'Create new',
    edit: 'Edit',
    delete: 'Delete',
    deleteDefaultTooltip: 'Set another format as default before deleting this one',
  },
};
